import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { formatDateTime, formatNumber } from '../../shared/format.js';
import { toast } from '../../shared/useToast.js';
import { usePlatformAuth } from './usePlatformAuth.js';
import {
  useCompanies,
  useApproveCompany,
  useRejectCompany,
  useSuspendCompany,
  useResumeCompany,
  useResetOwnerPassword,
  useDeleteCompany
} from './usePlatformCompanies.js';
import { StatusPill } from './components/StatusPill.jsx';
import { CopyBtn } from './components/CopyBtn.jsx';
import { CreateCompanyModal } from './components/CreateCompanyModal.jsx';
import { CredentialsModal } from './components/CredentialsModal.jsx';
import { CompanyDetailsModal } from './components/CompanyDetailsModal.jsx';
import { DeleteCompanyModal } from './components/DeleteCompanyModal.jsx';
import { ReasonModal } from './components/ReasonModal.jsx';

const TABS = [
  { key: '', label: 'الكل', stat: 'total' },
  { key: 'pending', label: 'بانتظار الموافقة', stat: 'pending' },
  { key: 'active', label: 'مفعّلة', stat: 'active' },
  { key: 'suspended', label: 'موقوفة', stat: 'suspended' },
  { key: 'rejected', label: 'مرفوضة', stat: 'rejected' }
];

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function pendingIdOf(m) {
  if (!m.isPending) return null;
  const v = m.variables;
  if (typeof v === 'string') return v;
  return v?.id ? String(v.id) : null;
}

export function PlatformDashboard() {
  const navigate = useNavigate();
  const user = usePlatformAuth((s) => s.user);
  const logout = usePlatformAuth((s) => s.logout);

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 350);

  // modal = { type: 'create' | 'details' | 'delete' | 'reject' | 'suspend', company }
  const [modal, setModal] = useState(null);
  const [credentials, setCredentials] = useState(null);

  const listQ = useCompanies({ status, q });
  const companies = listQ.data?.companies || [];
  const stats = listQ.data?.stats;

  const closeModal = () => setModal(null);
  const showCredentials = (creds, title, sub) => {
    if (!creds) return;
    setCredentials({ credentials: creds, title, sub });
  };

  const approve = useApproveCompany({
    onSuccess: (res) => {
      closeModal();
      showCredentials(res.credentials, 'تم التفعيل — بيانات دخول المالك');
    }
  });
  const reject = useRejectCompany({ onSuccess: closeModal });
  const suspend = useSuspendCompany({ onSuccess: closeModal });
  const resume = useResumeCompany();
  const resetPassword = useResetOwnerPassword({
    onSuccess: (res) => showCredentials(res.credentials, 'كلمة مرور جديدة لمالك الشركة')
  });
  const remove = useDeleteCompany({
    onSuccess: (res) => {
      closeModal();
      if (res.droppedData) toast.warning('تم حذف قاعدة بيانات الشركة نهائياً');
    }
  });

  const busyId = [approve, reject, suspend, resume, resetPassword, remove]
    .map(pendingIdOf)
    .find(Boolean) || null;

  async function doLogout() {
    await logout();
    toast.info('تم تسجيل الخروج');
    navigate('/platform/login', { replace: true });
  }

  const kpis = [
    { label: 'إجمالي الشركات', value: stats?.total, color: '#1A1A1A' },
    { label: 'بانتظار الموافقة', value: stats?.pending, color: stats?.pending ? '#EA580C' : '#6B7280' },
    { label: 'مفعّلة', value: stats?.active, color: '#16A34A' },
    { label: 'موقوفة', value: stats?.suspended, color: stats?.suspended ? '#B91C1C' : '#6B7280' }
  ];

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-white border-b border-line sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-3 flex-wrap">
          <img src="/kidzo-logo.png" alt="Kids Zone" className="h-9 object-contain" />
          <div className="flex-1 min-w-[160px]">
            <div className="font-extrabold text-[15px] leading-tight">لوحة مدير النظام</div>
            <div className="text-[11.5px] text-ink-3">إدارة شركات منصّة Kids Zone</div>
          </div>
          <div className="text-[12.5px] text-ink-2 font-semibold">
            {user?.fullName || user?.username || 'مدير النظام'}
          </div>
          <button type="button" className="btn btn-sm" onClick={() => navigate('/platform/change-password')}>
            <Icon name="settings" size={13} /> تغيير كلمة المرور
          </button>
          <button type="button" className="btn btn-sm" onClick={doLogout}>
            <Icon name="logout" size={13} /> خروج
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-5 pb-16 space-y-3.5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map((k) => (
            <div key={k.label} className="card p-3.5">
              <div className="text-[11.5px] text-ink-3">{k.label}</div>
              <div className="text-[20px] font-extrabold mt-1" style={{ color: k.color }}>
                {k.value == null ? '—' : formatNumber(k.value)}
              </div>
            </div>
          ))}
        </div>

        <Card>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] flex-wrap">
              {TABS.map((t) => (
                <button
                  key={t.key || 'all'}
                  type="button"
                  onClick={() => setStatus(t.key)}
                  className={clsx(
                    'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                    status === t.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
                  )}
                >
                  {t.label}
                  {stats && stats[t.stat] != null ? (
                    <span className={clsx('ms-1.5 text-[11px]', status === t.key ? 'opacity-90' : 'text-ink-3')}>
                      {formatNumber(stats[t.stat])}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="relative" style={{ width: 240 }}>
              <span className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3">
                <Icon name="search" size={15} />
              </span>
              <input
                className="input pr-9"
                placeholder="بحث بالاسم أو الكود أو المالك..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button type="button" className="btn ms-auto" onClick={() => listQ.refetch()} disabled={listQ.isFetching}>
              <Icon name="clock" size={13} /> {listQ.isFetching ? 'جاري التحديث...' : 'تحديث'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setModal({ type: 'create' })}>
              <Icon name="plus" size={14} /> شركة جديدة
            </button>
          </div>

          {listQ.isLoading ? (
            <div className="text-center py-10 text-ink-3">جاري التحميل...</div>
          ) : listQ.isError ? (
            <div className="text-center py-10">
              <div className="text-red font-semibold text-[13px]">تعذّر تحميل قائمة الشركات</div>
              <button type="button" className="btn btn-sm mt-3" onClick={() => listQ.refetch()}>إعادة المحاولة</button>
            </div>
          ) : !companies.length ? (
            <div className="text-center py-10 text-ink-3">
              {q || status ? (
                'لا توجد شركات مطابقة لهذا الفلتر'
              ) : (
                <>
                  <div>لا توجد شركات بعد</div>
                  <button type="button" className="btn btn-primary mt-3" onClick={() => setModal({ type: 'create' })}>
                    <Icon name="plus" size={14} /> إنشاء أول شركة
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-ink-3 border-b border-line text-right">
                    <th className="p-2">الشركة</th>
                    <th className="p-2">كود الشركة</th>
                    <th className="p-2">المالك</th>
                    <th className="p-2">الحالة</th>
                    <th className="p-2">آخر دخول</th>
                    <th className="p-2">تاريخ الإنشاء</th>
                    <th className="p-2">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => {
                    const id = String(c._id);
                    const busy = busyId === id;
                    return (
                      <tr key={id} className="border-b border-line">
                        <td className="p-2">
                          <div className="font-bold">{c.name}</div>
                          {c.notes ? <div className="text-[11px] text-ink-3 truncate max-w-[220px]">{c.notes}</div> : null}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono" dir="ltr">{c.slug}</span>
                            <CopyBtn value={c.slug} label="نسخ" title="نسخ كود الشركة" />
                          </div>
                        </td>
                        <td className="p-2">
                          <div>{c.owner?.fullName || '—'}</div>
                          <div className="text-[11px] text-ink-3" dir="ltr">{c.owner?.phone || ''}</div>
                        </td>
                        <td className="p-2"><StatusPill status={c.status} /></td>
                        <td className="p-2 text-ink-3">{c.lastLoginAt ? formatDateTime(c.lastLoginAt) : 'لم يدخل بعد'}</td>
                        <td className="p-2 text-ink-3">{formatDateTime(c.createdAt)}</td>
                        <td className="p-2">
                          <div className="flex gap-1 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-sm"
                              title="تفاصيل الشركة"
                              onClick={() => setModal({ type: 'details', company: c })}
                            >
                              <Icon name="reports" size={13} /> تفاصيل
                            </button>

                            {c.status === 'pending' ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  disabled={busy}
                                  onClick={() => approve.mutate(id)}
                                >
                                  <Icon name="check" size={13} /> موافقة وتفعيل
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  disabled={busy}
                                  onClick={() => setModal({ type: 'reject', company: c })}
                                >
                                  رفض
                                </button>
                              </>
                            ) : null}

                            {c.status === 'active' ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  disabled={busy}
                                  onClick={() => setModal({ type: 'suspend', company: c })}
                                >
                                  إيقاف
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  title="إنشاء كلمة مرور جديدة لمالك الشركة"
                                  disabled={busy}
                                  onClick={() => resetPassword.mutate(id)}
                                >
                                  <Icon name="settings" size={13} /> إعادة ضبط كلمة مرور المالك
                                </button>
                              </>
                            ) : null}

                            {c.status === 'suspended' ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                disabled={busy}
                                onClick={() => resume.mutate(id)}
                              >
                                <Icon name="check" size={13} /> استئناف
                              </button>
                            ) : null}

                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              title="حذف الشركة"
                              disabled={busy}
                              onClick={() => setModal({ type: 'delete', company: c })}
                            >
                              <Icon name="trash" size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {modal?.type === 'create' ? (
        <CreateCompanyModal
          onClose={closeModal}
          onCreated={(res) => {
            closeModal();
            showCredentials(res.credentials, 'تم الإنشاء والتفعيل — بيانات دخول المالك');
          }}
        />
      ) : null}

      {modal?.type === 'details' ? (
        <CompanyDetailsModal
          companyId={modal.company._id}
          fallback={modal.company}
          onClose={closeModal}
        />
      ) : null}

      {modal?.type === 'reject' ? (
        <ReasonModal
          kind="reject"
          company={modal.company}
          pending={reject.isPending}
          onClose={closeModal}
          onConfirm={(reason) => reject.mutate({ id: String(modal.company._id), reason })}
        />
      ) : null}

      {modal?.type === 'suspend' ? (
        <ReasonModal
          kind="suspend"
          company={modal.company}
          pending={suspend.isPending}
          onClose={closeModal}
          onConfirm={(reason) => suspend.mutate({ id: String(modal.company._id), reason })}
        />
      ) : null}

      {modal?.type === 'delete' ? (
        <DeleteCompanyModal
          company={modal.company}
          pending={remove.isPending}
          onClose={closeModal}
          onConfirm={({ dropData }) => remove.mutate({ id: String(modal.company._id), dropData })}
        />
      ) : null}

      {credentials ? (
        <CredentialsModal
          credentials={credentials.credentials}
          title={credentials.title}
          sub={credentials.sub}
          onDone={() => setCredentials(null)}
        />
      ) : null}
    </div>
  );
}
